'use client';
import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  forwardRef,
  useRef,
  useImperativeHandle,
  useDeferredValue,
} from 'react';
import { RiArrowDownSLine, RiSearchLine } from 'react-icons/ri';
import { createPortal } from 'react-dom';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Checkbox, FormLabel, Input } from '@mui/material';
import { cn } from '../Utlils';


const SINGLE_ROW_ESTIMATE = 40;
const MULTI_ROW_ESTIMATE = 52;

const MultiSelectDropdown = forwardRef(
  (
    {
      id = `dropdown-${Math.random().toString(36).substring(2, 11)}`,
      options,
      selected,
      setSelected,
      search = false,
      multiple = false,
      dropdownText = 'Select',
      renderItem = (opt) => <span>{opt.label}</span>,
      icon,
      position = 'top',
      width,
      dropdownFooter = false,
      onApply,
      disabled = false,
      onReset,
      footerAction,
      height = '200px',
      info,
      virtualizeThreshold = 50,
    },
    ref,
  ) => {
    const stableId = useRef(
      id || `dropdown-${Math.random().toString(36).substring(2, 11)}`,
    );
    const [searchQuery, setSearchQuery] = useState('');
    const deferredSearchQuery = useDeferredValue(searchQuery);

    const [dropdownMenu, setDropdownMenu] = useState(false);
    const buttonRef = useRef(null);
    const menuRef = useRef(null);
    const scrollRef = useRef(null);
    const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 0 });

    const [localSelected, setLocalSelected] = useState(selected);
    const dropdownRef = useRef(null);
    // useImperativeHandle(ref, () => dropdownRef.current!);

    const selectedKey =
      selected?.map((s) => `${s?.value}__${s?.label}`).join(',') ?? '';
    useEffect(() => {
      setLocalSelected(
        selected?.every((s) => !s?.value && !s?.label) ? [] : (selected ?? []),
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedKey]);

    const filteredOptions = useMemo(() => {
      if (!search) return options ?? [];
      return (options ?? []).filter((option) =>
        (option.label || '')
          .toString()
          .toLowerCase()
          .includes(deferredSearchQuery.toLowerCase()),
      );
    }, [options, search, deferredSearchQuery]);

    const selectedValueSet = useMemo(
      () => new Set(localSelected.map((item) => item?.value)),
      [localSelected],
    );

    const updateSelection = useCallback(
      (option) => {
        if (multiple) {
          setLocalSelected((prev) => {
            const exists = prev.some((item) => item.value === option.value);
            const updated = exists
              ? prev.filter((item) => item.value !== option.value)
              : [...prev, option];
            setSelected(updated);
            return updated;
          });
        } else {
          setLocalSelected([option]);
          setSelected([option]);
          setDropdownMenu(false);
        }
      },
      [multiple, setSelected],
    );

    const handleSelectAll = () => {
      const updated =
        localSelected.length === filteredOptions.length ? [] : filteredOptions;
      setLocalSelected(updated);
      setSelected(updated);
    };

    const handleReset = () => {
      onReset?.();
      setLocalSelected([]);
      setSelected([]);
      setDropdownMenu(false);
    };

    const calculateMenuPos = useCallback(() => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setMenuPos({
          top: position === 'bottom' ? rect.top - 210 : rect.bottom,
          left: rect.left,
          width: rect.width,
        });
      }
    }, [position]);

    useEffect(() => {
      if (!dropdownMenu) return;
      window.addEventListener('resize', calculateMenuPos);
      return () => window.removeEventListener('resize', calculateMenuPos);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dropdownMenu, calculateMenuPos]);

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target ) &&
          menuRef.current &&
          !menuRef.current.contains(event.target )
        ) {
          setDropdownMenu(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (!dropdownMenu) return;

      const handleScroll = () => setDropdownMenu(false);

      let parent = dropdownRef.current?.parentElement;
      const scrollableParents = [];

      while (parent && parent !== document.body) {
        const { overflow, overflowX, overflowY } = getComputedStyle(parent);
        const isScrollable = [overflow, overflowX, overflowY].some(
          (v) => v === 'auto' || v === 'scroll',
        );
        if (isScrollable) {
          scrollableParents.push(parent);
          parent.addEventListener('scroll', handleScroll);
        }
        parent = parent.parentElement;
      }

      return () => {
        scrollableParents.forEach((el) =>
          el.removeEventListener('scroll', handleScroll),
        );
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dropdownMenu]);

    const shouldVirtualize = filteredOptions.length > virtualizeThreshold;

    const rowVirtualizer = useVirtualizer({
      count: filteredOptions.length,
      getScrollElement: () => scrollRef.current,
      estimateSize: () => (multiple ? MULTI_ROW_ESTIMATE : SINGLE_ROW_ESTIMATE),
      overscan: 8,
      enabled: shouldVirtualize && dropdownMenu,
    });

    useEffect(() => {
      if (dropdownMenu && shouldVirtualize) {
        rowVirtualizer.measure();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dropdownMenu, shouldVirtualize, filteredOptions.length]);

    const renderRow = useCallback(
      (option) => {
        const isSelected = selectedValueSet.has(option.value);

        return multiple ? (
          <FormLabel
            className={cn(
              'has-[:checked]:bg-primary-50 has-[:checked]:border-primary-600 hover:bg-gray-50 flex flex-col py-[6px] px-[14px] cursor-pointer border-l-4 border-transparent',
              option?.disabledOption &&
                'opacity-50 cursor-not-allowed hover:bg-white text-gray-300 select-none',
            )}
            htmlFor={`${stableId.current}-checkbox-${option.value}`}
          >
            <section className="flex items-center justify-between gap-2 w-full">
              <div className="flex gap-2">
                <Checkbox
                  id={`${stableId.current}-checkbox-${option.value}`}
                  checked={isSelected}
                  onChange={() => updateSelection(option)}
                  disabled={option?.disabledOption}
                />
                <div className="flex items-center gap-1">
                  <div
                    style={{
                      color: option?.disabledOption
                        ? '#D1D5DB'
                        : option.labelTextColor,
                    }}
                    className={cn(
                      'break-words',
                      option?.disabledOption && 'text-gray-300',
                    )}
                  >
                    {renderItem(option)}
                  </div>
                </div>
              </div>
              <span className="text-gray-500">{option?.info}</span>
            </section>
            <span className="pt-[2px] text-sm text-gray-500">
              {option?.addInfo}
            </span>
          </FormLabel>
        ) : (
          <FormLabel
            htmlFor={`${stableId.current}-checkbox-${option.value}`}
            className={cn(
              'flex justify-between py-[6px] px-[14px] hover:bg-gray-50 gap-2 items-center border-l-4 border-transparent cursor-pointer',
              {
                'bg-primary-50 border-primary-600': isSelected,
                'opacity-50 cursor-not-allowed hover:bg-white text-gray-500':
                  option?.disabledOption,
              },
            )}
            onClick={() => !option?.disabledOption && updateSelection(option)}
          >
            <div
              style={{
                color: option?.disabledOption
                  ? '#D1D5DB'
                  : option.labelTextColor,
              }}
              className={cn(
                'break-words',
                option?.disabledOption && 'text-gray-300',
              )}
            >
              {renderItem(option)}
            </div>
            <span className="text-gray-500">{info}</span>
          </FormLabel>
        );
      },
      [multiple, selectedValueSet, updateSelection, renderItem, info],
    );

    return (
      <div
        id={stableId.current}
        ref={dropdownRef}
        className={cn(
          'relative bg-gray-25 shadow-[0px_1px_2px_0px_#1018280D] rounded-lg',
          !width && 'w-full',
          disabled && 'cursor-not-allowed opacity-50',
        )}
        style={{ width }}
      >
        <button
          ref={buttonRef}
          type="button"
          aria-haspopup="listbox"
          aria-expanded={dropdownMenu}
          aria-labelledby={`${stableId.current}-label`}
          disabled={disabled}
          onClick={() => {
            if (!disabled) {
              calculateMenuPos();
              setDropdownMenu((prev) => !prev);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              if (!disabled) {
                calculateMenuPos();
                setDropdownMenu((prev) => !prev);
              }
            }
          }}
          className={cn(
            'w-full hover:bg-gray-50 py-2 px-[14px] rounded-lg flex justify-between items-center text-gray-900 bg-gray-25 text-sm cursor-pointer',
            dropdownMenu
              ? 'border border-primary-600'
              : 'border border-gray-200',
            disabled && 'bg-gray-300 hover:bg-gray-300 cursor-not-allowed',
          )}
        >
          <section className="flex items-center gap-2 text-ellipsis overflow-hidden">
            <span
              id={`${stableId.current}-label`}
              className="line-clamp-1 w-full"
            >
              {multiple
                ? localSelected.length > 0
                  ? `${localSelected.length} Selected`
                  : dropdownText
                : localSelected[0]?.label || dropdownText}
            </span>
          </section>
          <RiArrowDownSLine aria-hidden="true" size={18} />
        </button>

        {dropdownMenu &&
          typeof window !== 'undefined' &&
          createPortal(
            <ul
              ref={menuRef}
              role="listbox"
              aria-multiselectable={multiple}
              aria-labelledby={`${stableId.current}-label`}
              style={{
                position: 'fixed',
                top: menuPos.top,
                left: menuPos.left,
                width: menuPos.width,
                zIndex: 1000001,
              }}
              className="mt-1 shadow-sm rounded text-sm bg-white border border-primary-600 h-fit"
            >
              {search && (
                <Input
                  id={`${stableId.current}-search`}
                  type="text"
                  placeholder="Search..."
                  aria-label="Search options"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="rounded rounded-b-none text-gray-800 bg-white w-full h-[35px] pl-3 border-none"
                  // endIcon={<RiSearchLine size={18} />}
                />
              )}

              {multiple && (
                <section className="py-[6px] px-[14px] flex justify-between items-center">
                  <button
                    type="button"
                    aria-label="Select all"
                    onClick={handleSelectAll}
                    className="text-sm hover:text-primary-700 text-primary-600 cursor-pointer"
                  >
                    Select all
                  </button>
                  <button
                    aria-label="Reset"
                    type="button"
                    className="text-sm text-warning-500 hover:text-warning-600"
                    onClick={handleReset}
                  >
                    Reset
                  </button>
                </section>
              )}

              {/* This is the scroll container both the plain path and the
                  virtualized path render into. */}
              <section
                ref={scrollRef}
                style={{ maxHeight: height }}
                className="z-[1000] transition-all duration-75 delay-100 ease-in-out overflow-y-scroll mainContainerScroll"
              >
                {filteredOptions.length === 0 ? (
                  <div className="py-3 px-[14px] text-gray-400">
                    No results found
                  </div>
                ) : shouldVirtualize ? (
                  // ---- VIRTUALIZED PATH (large lists) ----
                  <div
                    style={{
                      height: rowVirtualizer.getTotalSize(),
                      position: 'relative',
                      width: '100%',
                    }}
                  >
                    {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                      const option = filteredOptions[virtualRow.index];
                      return (
                        <div
                          key={option.value}
                          data-index={virtualRow.index}
                          ref={rowVirtualizer.measureElement}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            transform: `translateY(${virtualRow.start}px)`,
                          }}
                        >
                          {renderRow(option)}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  // ---- PLAIN PATH (small lists — identical to before) ----
                  filteredOptions.map((option) => (
                    <React.Fragment key={option.value}>
                      {renderRow(option)}
                    </React.Fragment>
                  ))
                )}
              </section>

              {footerAction && (
                <div className="py-2 mt-1 px-2 border-t">{footerAction}</div>
              )}

              {dropdownFooter && (
                <div className="flex justify-end border-t border-gray-200 px-[14px] py-[8px] text-sm">
                  <button
                    type="button"
                    className="text-primary-600 hover:text-primary-700"
                    onClick={() => {
                      onApply?.();
                      setDropdownMenu(false);
                    }}
                  >
                    Apply
                  </button>
                </div>
              )}
            </ul>,
            document.body,
          )}
      </div>
    );
  },
);

MultiSelectDropdown.displayName = 'MultiSelectDropdown';
export default MultiSelectDropdown;
